import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import QRCode from 'qrcode';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { useAuth } from '../AuthContext';

ModuleRegistry.registerModules([AllCommunityModule]);

const VIEW_MODE = {
  trans: 'trans',
  detail: 'detail',
  category: 'category',
};

const saleStorageKey = 'salesTransSelectedSaleId';

const toNum = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const formatCurrency = (value) => {
  const num = toNum(value, 0);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
};

const formatLabel = (value, fallback = '') => {
  const label = String(value || '').trim().replace(/_/g, ' ');
  if (!label) return fallback;
  return label.replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatTransactionNumber = (row) => {
  const number = Number(row?.sale_trans_number);
  if (Number.isInteger(number) && number > 0) {
    return `#${String(number).padStart(4, '0')}`;
  }
  const id = String(row?.sales_trans_id || row?.id || '').trim();
  return id ? `#${id.slice(0, 8)}` : '';
};

const slugify = (value) => String(value || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 80);

function SalesTrans() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, fetchWrapper } = useAuth();
  const clientId = user?.id || null;

  const initialSaleId = useMemo(() => {
    const search = new URLSearchParams(location.search || '');
    const fromQuery = String(search.get('saleId') || '').trim();
    if (fromQuery) return fromQuery;
    const fromStorage = String(sessionStorage.getItem(saleStorageKey) || '').trim();
    return fromStorage === 'all' ? '' : fromStorage;
  }, [location.search]);

  const [sales, setSales] = useState([]);
  const [selectedSaleId, setSelectedSaleId] = useState(initialSaleId);
  const [loadingSales, setLoadingSales] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [status, setStatus] = useState('');
  const [viewMode, setViewMode] = useState(VIEW_MODE.trans);
  const [transRows, setTransRows] = useState([]);
  const [itemRows, setItemRows] = useState([]);
  const [selectedTransId, setSelectedTransId] = useState('');
  const [detailStripePaymentUrl, setDetailStripePaymentUrl] = useState('');
  const [detailStripeQrDataUrl, setDetailStripeQrDataUrl] = useState('');
  const [detailStripeLoading, setDetailStripeLoading] = useState(false);
  const [detailCashLoading, setDetailCashLoading] = useState(false);
  const [detailActionStatus, setDetailActionStatus] = useState('');
  const gridRef = useRef(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const search = new URLSearchParams(location.search || '');
    const fromQuery = String(search.get('saleId') || '').trim();
    if (!fromQuery) return;
    setSelectedSaleId(fromQuery);
  }, [location.search]);

  useEffect(() => {
    sessionStorage.setItem(saleStorageKey, selectedSaleId || 'all');
  }, [selectedSaleId]);

  useEffect(() => {
    setSelectedTransId('');
  }, [selectedSaleId]);

  const fetchSales = useCallback(async () => {
    if (!clientId) {
      setSales([]);
      setLoadingSales(false);
      return;
    }

    setLoadingSales(true);
    try {
      const response = await fetchWrapper(
        `${apiUrl}/supabase/rpc/select_all_sales`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ p_client_id: clientId }),
        }
      );

      if (!response?.ok) {
        const errorData = await response?.json?.().catch(() => ({}));
        throw new Error(errorData?.error?.message || 'Failed to load sales.');
      }

      const rows = await response.json().catch(() => []);
      const normalized = (Array.isArray(rows) ? rows : [])
        .map((row) => ({
          id: row.id,
          sale_name: String(row.sale_name || '').trim(),
          start_date: row.start_date,
          end_date: row.end_date,
          status: String(row.status || '').trim().toLowerCase(),
        }))
        .filter((row) => row.id)
        .sort((a, b) => {
          const aTs = Date.parse(a?.start_date || '');
          const bTs = Date.parse(b?.start_date || '');
          const aValid = Number.isFinite(aTs);
          const bValid = Number.isFinite(bTs);
          if (aValid && bValid) return bTs - aTs;
          if (aValid) return -1;
          if (bValid) return 1;
          return String(a?.sale_name || '').localeCompare(String(b?.sale_name || ''), undefined, { sensitivity: 'base' });
        });

      setSales(normalized);
      setStatus('');
    } catch (err) {
      setSales([]);
      setStatus(err?.message || 'Failed to load sales.');
    } finally {
      setLoadingSales(false);
    }
  }, [apiUrl, clientId, fetchWrapper]);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  useEffect(() => {
    if (!selectedSaleId) return;
    if (sales.length === 0) return;
    const exists = sales.some((sale) => sale.id === selectedSaleId);
    if (!exists) {
      setSelectedSaleId('');
    }
  }, [sales, selectedSaleId]);

  const fetchTransData = useCallback(async () => {
    if (!clientId) {
      setTransRows([]);
      setItemRows([]);
      return;
    }

    setLoadingData(true);
    setStatus('Loading sales transactions...');

    const payload = {
      p_client_id: clientId,
      p_sale_id: selectedSaleId || null,
    };

    try {
      const [transResponse, itemsResponse] = await Promise.all([
        fetchWrapper(`${apiUrl}/supabase/rpc/select_sales_trans`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }),
        fetchWrapper(`${apiUrl}/supabase/rpc/select_sales_trans_items`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }),
      ]);

      const transData = await transResponse?.json?.().catch(() => []);
      const itemsData = await itemsResponse?.json?.().catch(() => []);

      if (!transResponse?.ok) {
        throw new Error(transData?.error?.message || 'Failed to load sales transactions.');
      }
      if (!itemsResponse?.ok) {
        throw new Error(itemsData?.error?.message || 'Failed to load transaction detail.');
      }

      setTransRows(Array.isArray(transData) ? transData : []);
      setItemRows(Array.isArray(itemsData) ? itemsData : []);
      setStatus('');
    } catch (err) {
      setTransRows([]);
      setItemRows([]);
      setStatus(err?.message || 'Failed to load transaction data.');
    } finally {
      setLoadingData(false);
    }
  }, [apiUrl, clientId, fetchWrapper, selectedSaleId]);

  useEffect(() => {
    fetchTransData();
  }, [fetchTransData]);

  const categorySummaryRows = useMemo(() => {
    const grouped = new Map();

    for (const row of itemRows) {
      const category = String(row?.category || '').trim() || 'Uncategorized';
      const quantity = Math.max(1, Math.floor(toNum(row?.quantity, 1)));
      const soldUnitPrice = toNum(row?.sold_unit_price, 0);
      const askingUnitPrice = toNum(row?.asking_unit_price, 0);
      const lineSubtotal = toNum(row?.line_subtotal, soldUnitPrice * quantity);
      const isManual = Boolean(row?.is_manual);

      const existing = grouped.get(category) || {
        category,
        line_count: 0,
        qty_total: 0,
        sold_total: 0,
        asking_total: 0,
        manual_line_count: 0,
      };

      existing.line_count += 1;
      existing.qty_total += quantity;
      existing.sold_total += lineSubtotal;
      existing.asking_total += askingUnitPrice * quantity;
      if (isManual) existing.manual_line_count += 1;

      grouped.set(category, existing);
    }

    return [...grouped.values()]
      .map((row) => ({
        ...row,
        avg_sold_unit_price: row.qty_total > 0 ? row.sold_total / row.qty_total : 0,
      }))
      .sort((a, b) => toNum(b.sold_total, 0) - toNum(a.sold_total, 0));
  }, [itemRows]);

  const transTotals = useMemo(() => transRows.reduce((acc, row) => {
    acc.count += 1;
    acc.itemCount += Math.max(0, Math.floor(toNum(row?.item_count, 0)));
    acc.subtotal += toNum(row?.subtotal_amount, 0);
    acc.tax += toNum(row?.tax_amount, 0);
    acc.total += toNum(row?.total_amount, 0);
    return acc;
  }, {
    count: 0,
    itemCount: 0,
    subtotal: 0,
    tax: 0,
    total: 0,
  }), [transRows]);

  const itemTotals = useMemo(() => itemRows.reduce((acc, row) => {
    const qty = Math.max(1, Math.floor(toNum(row?.quantity, 1)));
    acc.lineCount += 1;
    acc.qty += qty;
    acc.amount += toNum(row?.line_subtotal, 0);
    return acc;
  }, {
    lineCount: 0,
    qty: 0,
    amount: 0,
  }), [itemRows]);

  const categoryTotals = useMemo(() => categorySummaryRows.reduce((acc, row) => {
    acc.categoryCount += 1;
    acc.lineCount += Math.max(0, Math.floor(toNum(row?.line_count, 0)));
    acc.qty += Math.max(0, Math.floor(toNum(row?.qty_total, 0)));
    acc.amount += toNum(row?.sold_total, 0);
    return acc;
  }, {
    categoryCount: 0,
    lineCount: 0,
    qty: 0,
    amount: 0,
  }), [categorySummaryRows]);

  const selectedSale = useMemo(
    () => sales.find((sale) => sale.id === selectedSaleId) || null,
    [sales, selectedSaleId],
  );

  const selectedTrans = useMemo(
    () => transRows.find((row) => row.id === selectedTransId) || null,
    [selectedTransId, transRows],
  );

  const selectedTransLines = useMemo(
    () => itemRows.filter((row) => row.sales_trans_id === selectedTransId),
    [itemRows, selectedTransId],
  );

  const selectedTransItemCount = useMemo(() => selectedTransLines.reduce(
    (sum, row) => sum + Math.max(1, Math.floor(toNum(row?.quantity, 1))),
    0
  ), [selectedTransLines]);

  const selectedTransIsPendingStripe = useMemo(() => {
    const paymentMethod = String(selectedTrans?.payment_method || '').trim().toLowerCase();
    const transStatus = String(selectedTrans?.status || '').trim().toLowerCase();
    return paymentMethod === 'stripe_checkout' && transStatus === 'pending_payment';
  }, [selectedTrans]);

  useEffect(() => {
    if (!selectedTransId) return;
    if (!selectedTrans) setSelectedTransId('');
  }, [selectedTrans, selectedTransId]);

  const openTransactionDetails = useCallback((row) => {
    const id = String(row?.id || '').trim();
    if (!id) return;
    setSelectedTransId(id);
  }, []);

  const closeTransactionDetails = useCallback(() => {
    setSelectedTransId('');
  }, []);

  useEffect(() => {
    let cancelled = false;
    setDetailStripePaymentUrl('');
    setDetailStripeQrDataUrl('');
    setDetailActionStatus('');

    if (!selectedTransIsPendingStripe || !selectedTrans?.id) {
      setDetailStripeLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const loadStripeQr = async () => {
      setDetailStripeLoading(true);
      try {
        const response = await fetchWrapper(`${apiUrl}/stripe/create-sale-payment-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            salesTransId: selectedTrans.id,
            saleId: selectedTrans.sale_id,
          }),
        });
        const data = await response?.json?.().catch(() => ({}));
        if (!response?.ok) {
          throw new Error(data?.error || 'Failed to recreate payment QR.');
        }

        const paymentUrl = String(data?.url || '').trim();
        if (!paymentUrl) {
          throw new Error('Stripe payment URL was not returned.');
        }

        const qrDataUrl = await QRCode.toDataURL(paymentUrl, {
          errorCorrectionLevel: 'M',
          margin: 2,
          width: 260,
        });

        if (cancelled) return;
        setDetailStripePaymentUrl(paymentUrl);
        setDetailStripeQrDataUrl(qrDataUrl);
      } catch (err) {
        if (!cancelled) {
          setDetailActionStatus(err?.message || 'Failed to recreate payment QR.');
        }
      } finally {
        if (!cancelled) setDetailStripeLoading(false);
      }
    };

    loadStripeQr();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, fetchWrapper, selectedTrans?.id, selectedTrans?.sale_id, selectedTransIsPendingStripe]);

  const handleConvertPendingStripeToCash = useCallback(async () => {
    if (!selectedTransIsPendingStripe || !selectedTrans?.id) return;
    const confirmed = window.confirm(
      'Mark this pending QR transaction as paid by cash? The Stripe Checkout link will be expired if it is still open.'
    );
    if (!confirmed) return;

    setDetailCashLoading(true);
    setDetailActionStatus('');
    try {
      const response = await fetchWrapper(`${apiUrl}/stripe/convert-sale-payment-to-cash`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          salesTransId: selectedTrans.id,
        }),
      });
      const data = await response?.json?.().catch(() => ({}));
      if (!response?.ok) {
        throw new Error(data?.error || 'Failed to convert transaction to cash.');
      }

      setDetailActionStatus('Transaction marked paid by cash.');
      await fetchTransData();
    } catch (err) {
      setDetailActionStatus(err?.message || 'Failed to convert transaction to cash.');
    } finally {
      setDetailCashLoading(false);
    }
  }, [apiUrl, fetchTransData, fetchWrapper, selectedTrans?.id, selectedTransIsPendingStripe]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 110,
    flex: 1,
  }), []);

  const transColumnDefs = useMemo(() => [
    {
      field: 'created_at',
      headerName: 'Transaction Date',
      minWidth: 170,
      valueFormatter: (params) => formatDateTime(params.value),
    },
    {
      colId: 'sale_trans_number',
      headerName: 'Sale #',
      minWidth: 100,
      cellDataType: 'text',
      valueGetter: (params) => formatTransactionNumber(params?.data),
    },
    { field: 'sale_name', headerName: 'Sale', minWidth: 170 },
    {
      field: 'payment_method',
      headerName: 'Pay Type',
      minWidth: 115,
      valueFormatter: (params) => formatLabel(params.value),
    },
    {
      colId: 'is_tax_exempt_label',
      headerName: 'Tax Exempt',
      minWidth: 115,
      cellDataType: 'text',
      valueGetter: (params) => (params?.data?.is_tax_exempt ? 'Yes' : 'No'),
    },
    { field: 'tax_exempt_business_name', headerName: 'Tax Exempt Business', minWidth: 190 },
    { field: 'tax_exempt_tax_id', headerName: 'Tax Exempt ID', minWidth: 150 },
    { field: 'item_count', headerName: 'Items', minWidth: 90 },
    {
      field: 'subtotal_amount',
      headerName: 'Subtotal',
      minWidth: 120,
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      field: 'tax_amount',
      headerName: 'Tax',
      minWidth: 110,
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      field: 'total_amount',
      headerName: 'Total',
      minWidth: 120,
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 125,
      valueFormatter: (params) => formatLabel(params.value),
    },
    { field: 'id', headerName: 'Transaction ID', minWidth: 280 },
    {
      colId: 'details',
      headerName: 'Details',
      minWidth: 115,
      maxWidth: 130,
      pinned: 'right',
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <button
          type="button"
          onClick={() => openTransactionDetails(params?.data)}
          className="text-sm font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-200 rounded-sm"
        >
          Details
        </button>
      ),
    },
  ], [openTransactionDetails]);

  const itemColumnDefs = useMemo(() => [
    {
      field: 'trans_created_at',
      headerName: 'Transaction Date',
      minWidth: 170,
      valueFormatter: (params) => formatDateTime(params.value),
    },
    {
      colId: 'sale_trans_number',
      headerName: 'Sale #',
      minWidth: 100,
      cellDataType: 'text',
      valueGetter: (params) => formatTransactionNumber(params?.data),
    },
    { field: 'sale_name', headerName: 'Sale', minWidth: 170 },
    { field: 'title', headerName: 'Title', minWidth: 260 },
    { field: 'category', headerName: 'Category', minWidth: 150 },
    { field: 'quantity', headerName: 'Qty', minWidth: 90 },
    {
      field: 'asking_unit_price',
      headerName: 'Asking Unit',
      minWidth: 130,
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      field: 'sold_unit_price',
      headerName: 'Sold Unit',
      minWidth: 120,
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      field: 'line_subtotal',
      headerName: 'Line Total',
      minWidth: 120,
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      colId: 'is_manual_label',
      headerName: 'Manual',
      minWidth: 100,
      cellDataType: 'text',
      valueGetter: (params) => (params?.data?.is_manual ? 'Yes' : 'No'),
    },
    {
      field: 'payment_method',
      headerName: 'Pay Type',
      minWidth: 110,
      valueFormatter: (params) => formatLabel(params.value),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 125,
      valueFormatter: (params) => formatLabel(params.value),
    },
    {
      colId: 'is_tax_exempt_label',
      headerName: 'Tax Exempt',
      minWidth: 115,
      cellDataType: 'text',
      valueGetter: (params) => (params?.data?.is_tax_exempt ? 'Yes' : 'No'),
    },
    { field: 'sales_trans_id', headerName: 'Transaction ID', minWidth: 280 },
    { field: 'id', headerName: 'Line ID', minWidth: 280 },
  ], []);

  const categoryColumnDefs = useMemo(() => [
    { field: 'category', headerName: 'Category', minWidth: 220 },
    { field: 'line_count', headerName: 'Lines', minWidth: 90 },
    { field: 'qty_total', headerName: 'Qty', minWidth: 90 },
    {
      field: 'asking_total',
      headerName: 'Asking Total',
      minWidth: 130,
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      field: 'sold_total',
      headerName: 'Sold Total',
      minWidth: 130,
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      field: 'avg_sold_unit_price',
      headerName: 'Avg Sold Unit',
      minWidth: 130,
      valueFormatter: (params) => formatCurrency(params.value),
    },
    { field: 'manual_line_count', headerName: 'Manual Lines', minWidth: 120 },
  ], []);

  const currentRowData = useMemo(() => {
    if (viewMode === VIEW_MODE.detail) return itemRows;
    if (viewMode === VIEW_MODE.category) return categorySummaryRows;
    return transRows;
  }, [categorySummaryRows, itemRows, transRows, viewMode]);

  const currentColumnDefs = useMemo(() => {
    if (viewMode === VIEW_MODE.detail) return itemColumnDefs;
    if (viewMode === VIEW_MODE.category) return categoryColumnDefs;
    return transColumnDefs;
  }, [categoryColumnDefs, itemColumnDefs, transColumnDefs, viewMode]);

  const totalsNode = useMemo(() => {
    if (viewMode === VIEW_MODE.detail) {
      return (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800">
          Detail Totals: {itemTotals.lineCount} lines | {itemTotals.qty} qty | {formatCurrency(itemTotals.amount)}
        </div>
      );
    }
    if (viewMode === VIEW_MODE.category) {
      return (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800">
          Category Totals: {categoryTotals.categoryCount} categories | {categoryTotals.lineCount} lines | {categoryTotals.qty} qty | {formatCurrency(categoryTotals.amount)}
        </div>
      );
    }
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800">
        Transaction Totals: {transTotals.count} transactions | {transTotals.itemCount} items | Subtotal {formatCurrency(transTotals.subtotal)} | Tax {formatCurrency(transTotals.tax)} | Total {formatCurrency(transTotals.total)}
      </div>
    );
  }, [categoryTotals, itemTotals, transTotals, viewMode]);

  const currentViewLabel = useMemo(() => {
    if (viewMode === VIEW_MODE.detail) return 'Transaction Detail';
    if (viewMode === VIEW_MODE.category) return 'Category Summary';
    return 'Sales Transactions';
  }, [viewMode]);

  const handleExportCsv = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    const saleLabel = selectedSale?.sale_name || 'all-sales';
    const base = slugify(saleLabel) || 'all-sales';
    const viewLabel = viewMode === VIEW_MODE.trans
      ? 'transactions'
      : viewMode === VIEW_MODE.detail
        ? 'transaction-detail'
        : 'category-summary';

    api.exportDataAsCsv({
      fileName: `${viewLabel}-${base}.csv`,
      prependContent: `Scucci Sales ${currentViewLabel}\nGenerated on ${new Date().toLocaleString()}\n`,
    });
  }, [currentViewLabel, selectedSale?.sale_name, viewMode]);

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
      <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-4 sm:p-6 mx-auto max-w-[1400px]">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Sales Transactions</h1>
              <p className="text-sm text-slate-500 mt-1">View transaction headers, line detail, and category totals with CSV export.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => navigate(`/estate-sales-pipeline/sold-inventory${selectedSaleId ? `?saleId=${selectedSaleId}` : ''}`)}
                className="h-10 px-4 rounded-lg border border-slate-300 text-slate-900 font-semibold text-sm hover:bg-slate-50"
              >
                Back to Sales Tracker
              </button>
              <button
                type="button"
                onClick={() => navigate('/estate-sales-pipeline/sales-stats')}
                className="h-10 px-4 rounded-lg border border-slate-300 text-slate-900 font-semibold text-sm hover:bg-slate-50"
              >
                Back to Sales Stats
              </button>
              <button
                type="button"
                onClick={() => navigate('/estate-sales-pipeline/userdashboard')}
                className="h-10 px-4 rounded-lg border border-slate-300 text-slate-900 font-semibold text-sm hover:bg-slate-50"
              >
                Back to Dashboard
              </button>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-end gap-3">
            <div className="w-full lg:w-[22rem]">
              <label htmlFor="sales-trans-sale-select" className="block text-sm font-semibold text-slate-700 mb-1.5">
                Sale
              </label>
              <select
                id="sales-trans-sale-select"
                value={selectedSaleId}
                onChange={(e) => setSelectedSaleId(e.target.value)}
                disabled={loadingSales}
                className="w-full h-11 rounded-lg border border-slate-300 px-3 text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <option value="">All Sales</option>
                {sales.map((sale) => (
                  <option key={sale.id} value={sale.id}>
                    {sale.sale_name || 'Unnamed Sale'}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {viewMode === VIEW_MODE.trans && (
                <button
                  type="button"
                  onClick={() => setViewMode(VIEW_MODE.detail)}
                  className="h-10 px-4 rounded-lg bg-[#0047AB] text-white font-semibold text-sm hover:bg-[#003B8F]"
                >
                  Transaction detail
                </button>
              )}

              {viewMode === VIEW_MODE.detail && (
                <>
                  <button
                    type="button"
                    onClick={() => setViewMode(VIEW_MODE.trans)}
                    className="h-10 px-4 rounded-lg border border-slate-300 text-slate-900 font-semibold text-sm hover:bg-slate-50"
                  >
                    Back to Transactions
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode(VIEW_MODE.category)}
                    className="h-10 px-4 rounded-lg bg-[#0047AB] text-white font-semibold text-sm hover:bg-[#003B8F]"
                  >
                    See Category Summary
                  </button>
                </>
              )}

              {viewMode === VIEW_MODE.category && (
                <button
                  type="button"
                  onClick={() => setViewMode(VIEW_MODE.detail)}
                  className="h-10 px-4 rounded-lg border border-slate-300 text-slate-900 font-semibold text-sm hover:bg-slate-50"
                >
                  Back to Transaction Detail
                </button>
              )}

              <button
                type="button"
                onClick={handleExportCsv}
                className="h-10 px-4 rounded-lg border border-slate-300 text-slate-900 font-semibold text-sm hover:bg-slate-50"
              >
                Export CSV
              </button>
            </div>
          </div>

          <div className="text-sm font-semibold text-slate-700">Current View: {currentViewLabel}</div>
          {totalsNode}

          {status && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {status}
            </div>
          )}

          <div className="ag-theme-quartz" style={{ width: '100%', minHeight: 610, height: 610 }}>
            <AgGridReact
              key={viewMode}
              ref={gridRef}
              rowData={currentRowData}
              columnDefs={currentColumnDefs}
              defaultColDef={defaultColDef}
              animateRows
            />
          </div>

          {!loadingData && currentRowData.length === 0 && !status && (
            <div className="text-sm text-slate-500">No rows found for this view.</div>
          )}
        </div>
      </div>
      {selectedTrans && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 max-[600px]:p-0"
          role="dialog"
          aria-modal="true"
          aria-label={`Transaction ${formatTransactionNumber(selectedTrans)} details`}
          onClick={closeTransactionDetails}
        >
          <div
            className="w-full max-w-lg rounded-[8px] bg-white shadow-xl max-h-[92vh] overflow-y-auto flex flex-col max-[600px]:max-w-none max-[600px]:h-[100dvh] max-[600px]:max-h-[100dvh] max-[600px]:rounded-none max-[600px]:shadow-none"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative px-6 pt-4 max-[600px]:px-4">
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={closeTransactionDetails}
                  className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
                  aria-label="Close transaction details"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="px-6 pb-6 text-center max-[600px]:px-4">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="mt-4 text-sm font-semibold uppercase tracking-[0.16em] text-gray-500">Sale Recorded</p>
              <p className="mt-2 text-6xl font-bold tracking-tight text-gray-900">{formatCurrency(selectedTrans.total_amount)}</p>
              <p className="mt-2 text-base text-gray-600">
                Amount to Collect (Includes {formatCurrency(selectedTrans.tax_amount)} Tax)
              </p>
              <p className="mt-1 text-base font-medium text-gray-700">
                Payment: {formatLabel(selectedTrans.payment_method, 'Card')}
              </p>
              <div className="mt-3 flex flex-wrap justify-center gap-2 text-sm">
                <span className="rounded-full bg-slate-100 px-3 py-1 font-semibold text-slate-700">
                  Transaction {formatTransactionNumber(selectedTrans)}
                </span>
                <span className="rounded-full bg-blue-50 px-3 py-1 font-semibold text-blue-800">
                  Status: {formatLabel(selectedTrans.status, 'Completed')}
                </span>
              </div>
              <div className="mt-3 text-sm text-gray-600">
                <div>{selectedTrans.sale_name || 'Unnamed Sale'}</div>
                <div>{formatDateTime(selectedTrans.created_at)}</div>
                {selectedTrans.is_tax_exempt && (
                  <div className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-amber-800">
                    Tax Exempt: {selectedTrans.tax_exempt_business_name || 'Business'} / {selectedTrans.tax_exempt_tax_id || 'Tax ID'}
                  </div>
                )}
              </div>
              {selectedTransIsPendingStripe && (
                <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 px-4 py-4">
                  <p className="text-sm font-semibold uppercase tracking-[0.12em] text-blue-900">
                    Customer Payment QR
                  </p>
                  <p className="mt-1 text-sm text-blue-900">
                    Have the customer scan this code, complete payment, and show the confirmation screen.
                  </p>
                  <div className="mt-3 flex justify-center">
                    {detailStripeQrDataUrl ? (
                      <img
                        src={detailStripeQrDataUrl}
                        alt="Stripe payment QR code"
                        className="h-[220px] w-[220px] rounded-lg bg-white p-2 shadow-sm"
                      />
                    ) : (
                      <div className="flex h-[220px] w-[220px] items-center justify-center rounded-lg bg-white text-sm text-gray-500 shadow-sm">
                        {detailStripeLoading ? 'Creating QR...' : 'QR unavailable'}
                      </div>
                    )}
                  </div>
                  {detailStripePaymentUrl && (
                    <a
                      href={detailStripePaymentUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-3 inline-block text-sm font-semibold text-blue-700 underline"
                    >
                      Open Card payment page
                    </a>
                  )}
                  <div className="mt-4 border-t border-blue-100 pt-3">
                    <p className="text-sm text-blue-900">
                      If the customer pays cash instead, mark this ticket as cash paid.
                    </p>
                    <button
                      type="button"
                      onClick={handleConvertPendingStripeToCash}
                      disabled={detailCashLoading}
                      className="mt-2 rounded-[8px] border border-blue-300 bg-white px-4 py-2 text-sm font-semibold text-blue-800 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {detailCashLoading ? 'Updating...' : 'Mark Paid By Cash'}
                    </button>
                  </div>
                </div>
              )}
              {detailActionStatus && (
                <p className="mt-3 rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-700">
                  {detailActionStatus}
                </p>
              )}
            </div>

            <div className="border-t border-[#E2E8F0]" />

            <div className="max-h-[240px] overflow-y-auto bg-[#F8F9FA] px-6 py-4 max-[600px]:px-4">
              <p className="mb-3 text-sm font-semibold uppercase tracking-[0.14em] text-gray-500">
                {selectedTransItemCount || toNum(selectedTrans.item_count, 0)} Items
              </p>
              {selectedTransLines.length > 0 ? (
                <div className="space-y-2">
                  {selectedTransLines.map((item, index) => {
                    const quantity = Math.max(1, Math.floor(toNum(item?.quantity, 1)));
                    const lineAmount = toNum(
                      item?.line_subtotal,
                      quantity * toNum(item?.sold_unit_price, 0)
                    );
                    return (
                      <div key={item?.id || `${item?.sales_trans_id}-${index}`} className="flex items-start justify-between gap-3 text-base">
                        <div className="min-w-0 text-gray-700">
                          <span className="mr-2 font-semibold text-gray-900">{quantity}x</span>
                          <span className="break-words">{item?.title || 'Untitled item'}</span>
                          {item?.category && (
                            <div className="ml-8 text-sm text-gray-500">{item.category}</div>
                          )}
                        </div>
                        <div className="shrink-0 font-semibold text-gray-900">{formatCurrency(lineAmount)}</div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No line items found for this transaction.</p>
              )}
            </div>

            <div className="border-t border-[#E2E8F0] bg-white px-6 py-4 max-[600px]:px-4">
              <div className="space-y-1 text-sm text-gray-700">
                <div className="flex justify-between gap-3">
                  <span>Subtotal</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(selectedTrans.subtotal_amount)}</span>
                </div>
                {toNum(selectedTrans.discount_pct_applied, 0) > 0 && (
                  <div className="flex justify-between gap-3">
                    <span>Discount</span>
                    <span className="font-semibold text-gray-900">{toNum(selectedTrans.discount_pct_applied, 0)}%</span>
                  </div>
                )}
                <div className="flex justify-between gap-3">
                  <span>Sales Tax</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(selectedTrans.tax_amount)}</span>
                </div>
                <div className="flex justify-between gap-3 border-t border-slate-200 pt-2 text-base">
                  <span className="font-semibold text-gray-900">Total</span>
                  <span className="font-bold text-gray-900">{formatCurrency(selectedTrans.total_amount)}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={closeTransactionDetails}
                className="mt-4 w-full rounded-[8px] bg-[#0047AB] px-4 py-3 text-lg font-semibold text-white hover:bg-[#003B8F] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SalesTrans;
